// ─────────────────────────────────────────────────────────────────────────────
// FtueInspectorComponent
// ─────────────────────────────────────────────────────────────────────────────
// Dev-only panel: every registered tip, its trigger, whether its anchor
// resolves on this page right now, its seen state, and a button to fire it.
// Without it, "why did that tip not show?" is unanswerable from the outside —
// eligibility depends on seen state, budget, cooldown and anchor presence.
//
// Gate: the `wolsal.ui.ftue.inspector` localStorage flag set to '1', and never
// on PROD. The flag rather than a build constant so it can be turned on against
// preprod without a rebuild.
//
// Copy here is intentionally NOT translated: this panel never reaches a
// customer, and 40 dev-only strings across three locales is noise in the
// translation files.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { FtueAnchorRegistryService } from 'src/app/core/ftue/ftue-anchor-registry.service';
import { FtuePresenterService } from 'src/app/core/ftue/ftue-presenter.service';
import { FTUE_TIPS } from 'src/app/core/ftue/ftue-tip.registry';
import { IFtueTip } from 'src/app/core/ftue/ftue.models';
import { IconComponent } from '../../icon/icon.component';

/** Flip to '1' in localStorage to show the panel. */
export const FTUE_INSPECTOR_FLAG = 'wolsal.ui.ftue.inspector';

@Component({
  selector: 'ui-ftue-inspector',
  standalone: true,
  imports: [NgClass, NgFor, NgIf, IconComponent],
  templateUrl: './ftue-inspector.component.html',
  styleUrls: ['./ftue-inspector.component.scss'],
})
export class FtueInspectorComponent implements OnInit, OnDestroy {

  readonly tips = FTUE_TIPS;

  isEnabled = false;
  isOpen = false;
  tipsEnabled = true;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly presenter: FtuePresenterService,
    private readonly anchors: FtueAnchorRegistryService,
  ) {}

  ngOnInit(): void {
    this.isEnabled = this.readFlag() && environment.env_name !== 'PROD';
    if (!this.isEnabled) {
      return;
    }
    this.presenter.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => (this.tipsEnabled = state.tipsEnabled));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Panel ──────────────────────────────────────────────────────────────────

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  // ── Per-tip state ──────────────────────────────────────────────────────────

  hasSeen(tip: IFtueTip): boolean {
    return this.presenter.hasSeen(tip);
  }

  anchorResolves(tip: IFtueTip): boolean {
    return this.anchors.isResolvable(tip.anchor);
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /** Goes through every eligibility rule, exactly like a real trigger. */
  fire(tip: IFtueTip): void {
    this.presenter.request(tip.id);
  }

  /** Bypasses the rules — the same path as Settings → Preview. */
  preview(tip: IFtueTip): void {
    this.presenter.preview(tip.id);
  }

  resetHistory(): void {
    this.presenter.resetHistory();
  }

  toggleTips(): void {
    this.presenter.setTipsEnabled(!this.tipsEnabled);
  }

  private readFlag(): boolean {
    try {
      return localStorage.getItem(FTUE_INSPECTOR_FLAG) === '1';
    } catch {
      return false;
    }
  }
}
